import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { completeMerchantProfile } from "../../../Redux_Toolkit/Auth/AuthThunk";
import { lookupPostcode } from "../../../utils/postcodesApi";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isValidPhone = (phone) => /^\d{11}$/.test(phone);
const isValidBusinessName = (name) => /^(?=.*[A-Za-z])[A-Za-z\s'-]+$/.test(name);
const isValidUkPostcode = (value) =>
  /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i.test(value);

const getFieldValidationError = (field, value) => {
  const trimmed = (value || "").trim();

  switch (field) {
    case "businessName":
      if (!trimmed) {
        return "Business name is required.";
      }
      if (trimmed.length < 2) {
        return "Business name must be at least 2 characters.";
      }
      if (!isValidBusinessName(trimmed)) {
        return "Business name can contain letters only (no digits).";
      }
      return "";

    case "email":
      if (!trimmed) {
        return "Email is required.";
      }
      if (!isValidEmail(trimmed)) {
        return "Please enter a valid email address.";
      }
      return "";

    case "phone":
      if (!trimmed) {
        return "Phone number is required.";
      }
      if (!isValidPhone(trimmed)) {
        return "Phone number must be exactly 11 digits.";
      }
      return "";

    case "postalCode":
      if (!trimmed) {
        return "Postal code is required.";
      }
      if (!isValidUkPostcode(trimmed)) {
        return "Please enter a valid UK postcode.";
      }
      return "";

    case "description":
      if (trimmed.length > 300) {
        return "Description must be 300 characters or less.";
      }
      return "";

    default:
      return "";
  }
};

function MerchantProfileModal({ isOpen, contact, identifierType, onComplete }) {
  const dispatch = useDispatch();

  const initialValues = useMemo(() => {
    return {
      businessName: "",
      email: identifierType === "email" ? contact : "",
      phone: identifierType === "phone" ? contact : "",
      postalCode: "",
      description: "",
    };
  }, [contact, identifierType]);

  const [formData, setFormData] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    setFormData(initialValues);
    setErrors({});
    setTouched({});
    setApiError("");
    setIsSubmitting(false);
  }, [initialValues]);

  if (!isOpen) {
    return null;
  }

  const normalizeFieldValue = (field, value) => {
    if (field === "businessName") {
      return value.replace(/[^a-zA-Z\s'-]/g, "").slice(0, 80);
    }

    if (field === "email") {
      return value.toLowerCase().trimStart();
    }

    if (field === "phone") {
      return value.replace(/\D/g, "").slice(0, 11);
    }

    if (field === "postalCode") {
      return value.toUpperCase().replace(/[^A-Z0-9\s]/g, "").slice(0, 8);
    }

    return value;
  };

  const updateField = (field, rawValue) => {
    const value = normalizeFieldValue(field, rawValue);

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    const validationError = getFieldValidationError(field, value);
    setErrors((prev) => ({
      ...prev,
      [field]: validationError || undefined,
    }));

    setApiError("");
  };

  const handleChange = (field) => (event) => {
    updateField(field, event.target.value);
  };

  const handleBlur = (field) => () => {
    const value = formData[field];
    const validationError = getFieldValidationError(field, value);

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: validationError || undefined,
    }));
  };

  const validate = () => {
    const fieldsToValidate = ["businessName", "email", "phone", "postalCode", "description"];
    const nextErrors = {};

    fieldsToValidate.forEach((field) => {
      const validationError = getFieldValidationError(field, formData[field]);
      if (validationError) {
        nextErrors[field] = validationError;
      }
    });

    setErrors(nextErrors);
    setTouched({
      businessName: true,
      email: true,
      phone: true,
      postalCode: true,
      description: true,
    });

    return Object.keys(nextErrors).length === 0;
  };

  const getInputClassName = (field, isReadOnly = false) => {
    const classNames = [];
    if (isReadOnly) {
      classNames.push("readonly-field");
    }
    if (touched[field] && errors[field]) {
      classNames.push("input-error");
    }

    return classNames.join(" ");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError("");

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    // Verify the postcode is real + active via postcodes.io BEFORE the backend
    // call, and resolve lat/lng so the server never re-geocodes (frontend is the
    // source of truth, same as the General Settings tab). On failure, show the
    // reason under the postcode field and stop — no backend call.
    const lookup = await lookupPostcode(formData.postalCode);
    if (!lookup.ok) {
      setTouched((prev) => ({ ...prev, postalCode: true }));
      setErrors((prev) => ({ ...prev, postalCode: lookup.reason }));
      setIsSubmitting(false);
      return;
    }

    try {
      await dispatch(
        completeMerchantProfile({
          businessName: formData.businessName.trim(),
          email: formData.email?.trim().toLowerCase() || undefined,
          phone: formData.phone?.trim() || undefined,
          postalCode: lookup.postcode,
          latitude: lookup.latitude,
          longitude: lookup.longitude,
          description: formData.description?.trim() || undefined,
        })
      ).unwrap();

      onComplete();
    } catch (err) {
      const message =
        err?.message ||
        err?.error?.message ||
        err?.error ||
        "Unable to complete merchant profile.";
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-completion-overlay" role="dialog" aria-modal="true">
      <div className="profile-completion-card">
        <h2 className="profile-completion-title">Profile Information</h2>
        <p className="profile-completion-subtitle">
          Enter your details to complete your merchant profile.
        </p>

        <form onSubmit={handleSubmit} className="profile-completion-form">
          <div className="profile-form-group">
            <label htmlFor="merchant-business-name">Business Name</label>
            <input
              id="merchant-business-name"
              type="text"
              value={formData.businessName}
              onChange={handleChange("businessName")}
              onBlur={handleBlur("businessName")}
              placeholder="Business Name"
              className={getInputClassName("businessName")}
              maxLength={80}
            />
            {touched.businessName && errors.businessName ? (
              <span className="profile-field-error">{errors.businessName}</span>
            ) : null}
          </div>

          <div className="profile-form-group">
            <label htmlFor="merchant-email">Email</label>
            <input
              id="merchant-email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              placeholder="business@email.com"
              readOnly={identifierType === "email"}
              className={getInputClassName("email", identifierType === "email")}
              maxLength={100}
            />
            {touched.email && errors.email ? (
              <span className="profile-field-error">{errors.email}</span>
            ) : null}
          </div>

          <div className="profile-form-group">
            <label htmlFor="merchant-phone">Phone</label>
            <input
              id="merchant-phone"
              type="text"
              value={formData.phone}
              onChange={handleChange("phone")}
              onBlur={handleBlur("phone")}
              placeholder="Phone Number (11 digits)"
              readOnly={identifierType === "phone"}
              className={getInputClassName("phone", identifierType === "phone")}
              inputMode="numeric"
            />
            {touched.phone && errors.phone ? (
              <span className="profile-field-error">{errors.phone}</span>
            ) : null}
          </div>

          <div className="profile-form-group">
            <label htmlFor="merchant-postal-code">Postal Code</label>
            <input
              id="merchant-postal-code"
              type="text"
              value={formData.postalCode}
              onChange={handleChange("postalCode")}
              onBlur={handleBlur("postalCode")}
              placeholder="Postcode (e.g. SK12 1QT)"
              className={getInputClassName("postalCode")}
            />
            {touched.postalCode && errors.postalCode ? (
              <span className="profile-field-error">{errors.postalCode}</span>
            ) : null}
          </div>

          <div className="profile-form-group">
            <label htmlFor="merchant-description">Description</label>
            <textarea
              id="merchant-description"
              value={formData.description}
              onChange={handleChange("description")}
              onBlur={handleBlur("description")}
              placeholder="Tell users about your business"
              rows={3}
              className={getInputClassName("description")}
              maxLength={300}
            />
            {touched.description && errors.description ? (
              <span className="profile-field-error">{errors.description}</span>
            ) : null}
          </div>

          {apiError ? <p className="profile-api-error">{apiError}</p> : null}

          <button type="submit" className="hybrid-primary-btn" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MerchantProfileModal;
